﻿<script setup lang="ts">
    import { reactive } from 'vue';
    import { areas, keywords_condition } from '@views/MediaMonitors/MediaMonitors.access';
    import BSFormInputText from '@shared/components/BSFormInputText.vue';
    import InputCheckbox from '@shared/components/InputCheckbox.vue';
    import InputRadio from '@shared/components/InputRadio.vue';

    type SearchSettingsProps = {
        areas: areas[];
        keywords_condition: keywords_condition;
        keywords: string[];
        validate: boolean;
    };

    const props = defineProps<SearchSettingsProps>();

    const state = reactive<{
        availableAreas: {
            label: string;
            value: areas;
        }[];
        newKeyword: string;
    }>({
        availableAreas: [
            { label: $Resources.MediaMonitorAreasSELabel, value: 'se' },
            { label: $Resources.MediaMonitorAreasNOLabel, value: 'no' },
            { label: $Resources.MediaMonitorAreasDKLabel, value: 'dk' },
            // { label: '[Social Media (fi)]', value: 'some-fi' } // not working with API
        ],
        newKeyword: '',
    });

    const emit = defineEmits(['update:areas', 'update:keywords']);

    const updateAreas = (checked: boolean, value: areas) => {
        let newValue = [...props.areas];
        checked ? newValue.push(value) : newValue.splice(newValue.indexOf(value), 1);
        emit('update:areas', newValue);
    };

    const addKeyword = () => {
        let newValue = [...props.keywords];
        newValue.push(state.newKeyword);
        emit('update:keywords', newValue);
        state.newKeyword = '';
    };

    const removeKeyword = (keyword: string) => {
        let newValue = [...props.keywords];
        newValue.splice(newValue.indexOf(keyword), 1);
        emit('update:keywords', newValue);
    };
</script>

<script lang="ts"></script>

<template>
    <fieldset class="fieldset">
        <legend>{{ $Resources.MediaMonitorSearchSettingsHeading }}</legend>
        <p class="m-lg-b">{{ $Resources.MediaMonitorRequiredInfo }}</p>

        <!--
		<legend>[Source selection:]</legend>
		<div class="form-group">
			<span class="label-look">[Search from these areas:] *</span>
			<SelectMany mode="edit" :list="state.availableAreas" :selectedItems="props.areas" selectionProp="value" displayProp="label" selectOnly="value" />
		</div>
        -->

        <h4>{{ $Resources.MediaMonitorAreasHeading }}: *</h4>
        <div class="m-sm-b d-flex gap-lg flex-wrap">
            <strong>{{ $Resources.MediaMonitorAreasLabel }}:</strong>
            <template v-for="(availableArea, index) in state.availableAreas">
                <InputCheckbox
                    :checked="props.areas.includes(availableArea.value)"
                    :required="index === 0 && props.areas.length === 0"
                    :group="props.areas"
                    invalidMessage="[Select at least one area.]"
                    @update:checked="(newValue: boolean) => updateAreas(newValue, availableArea.value)">
                    <template #label>{{ availableArea.label }}</template>
                </InputCheckbox>
            </template>
        </div>

        <h4>{{ $Resources.MediaMonitorKeywordsHeading }}: *</h4>

        <div class="m-sm-b d-flex gap-lg flex-wrap">
            <strong>{{ $Resources.MediaMonitorKeywordsLabel }}:</strong>
            <InputRadio
                :checked="props.keywords_condition === 'OR'"
                value="OR"
                name="keywords_condition"
                @update:checked="(newVal) => $emit('update:keywords_condition', newVal)">
                <template #label>{{ $Resources.MediaMonitorKeywordAnyLabel }}</template>
            </InputRadio>
            <InputRadio
                :checked="props.keywords_condition === 'AND'"
                value="AND"
                name="keywords_condition"
                @update:checked="(newVal) => $emit('update:keywords_condition', newVal)">
                <template #label>{{ $Resources.MediaMonitorKeywordAllLabel }}</template>
            </InputRadio>
        </div>

        <div v-show="props.keywords.length > 0">
            <ul class="list-unstyled d-flex gap-sm flex-wrap align-items-center m-lg-b fw-600">
                <template v-for="(keyword, index) in props.keywords">
                    <li class="keyword bg-primary c-white">
                        {{ keyword }}
                        <button
                            type="button"
                            :aria-label="$Resources.MediaMonitorRemoveKeywordLabel"
                            class="btn btn-subtle keyword-remove p-relative"
                            @click="removeKeyword(keyword)">
                            <span aria-hidden="true">×</span>
                        </button>
                    </li>
                    <li v-show="index + 1 !== props.keywords.length">
                        {{ $Resources['MediaMonitorKeywordCondition' + props.keywords_condition] }}
                    </li>
                </template>
            </ul>
        </div>

        <div class="alert alert-danger alert-fixed-i" v-show="props.validate && props.keywords.length === 0">
            <p>
                <i class="fa fa-ban" aria-hidden="true"></i>
                <strong>{{ $Resources.MediaMonitorNoKeywordsAlertHeading }}</strong>
                {{ $Resources.MediaMonitorNoKeywordsAlertText }}
            </p>
        </div>

        <BSFormInputText
            class="form-group max-w-600"
            v-model:modelValue="state.newKeyword"
            :label="$Resources.MediaMonitorAddKeywordLabel"
            :placeholder="$Resources.MediaMonitorAddKeywordPlaceholder">
            <template #button>
                <button type="button" class="btn btn-primary" :disabled="state.newKeyword.length === 0" @click="addKeyword">
                    {{ $Resources.MediaMonitorAddKeywordBtn }}
                </button>
            </template>
        </BSFormInputText>
    </fieldset>
</template>

<style lang="less">
    /*
	:root {
		font-size: 16px;
	}
    */

    .keyword {
        padding: 5px 5px 5px 15px;
        line-height: 1;
        border-radius: 2px;
    }

    .keyword-remove {
        padding: 0 5px;
        font-size: 24px;
        line-height: 1;
        top: -1px;
        opacity: 0.5;

        &:hover {
            opacity: 1;
        }
    }
</style>

<style scoped lang="less"></style>
